import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import View from '@ckeditor/ckeditor5-ui/src/view';
import { Element } from '@ckeditor/ckeditor5-engine';

export class LineNumberAndCharCounterIndicator extends Plugin {
    previousEditorData: string = '';
    previousEditorWrappedText: any[] = [];
    selectionBeforeCursorPosition: any;
    hasTooltipError: boolean = false;
    private isRestoringContent: boolean = false; // Flag to prevent recursive calls during content restoration
    private previousLineCount: number = 0;

    init() {
        const editor = this.editor;
        const lineNumberAndCharCounterIndicator = 'lineNumberAndCharCounterIndicator';

        const getConfig = () => {
            const maxCharCount = Number(editor?.config?.get('lineNumberAndCharCounterConfig.maxCharCount') ?? -1);
            const maxBulletCharCount = Number(
                editor.config.get('lineNumberAndCharCounterConfig.maxBulletCharCount') ?? -1
            );
            const maxLineCount = Number(editor.config.get('lineNumberAndCharCounterConfig.maxLineCount') ?? -1);
            return { maxCharCount, maxBulletCharCount, maxLineCount };
        };

        editor.ui.componentFactory.add(lineNumberAndCharCounterIndicator, () => {
            const parentView = new View();

            parentView.setTemplate({
                tag: 'div',
                attributes: {
                    class: ['ck-line-number-and-char-counter-indicator']
                },
                children: ['']
            });

            const updateLineNumberAndCharCounterIndicator = () => {
                // Prevent recursive calls when we're restoring content
                if (this.isRestoringContent) {
                    return;
                }

                const { maxCharCount, maxBulletCharCount, maxLineCount } = getConfig();
                try {
                    let hasOverflow = false;
                    // Wrap text with constraints
                    const simulatedWrappedContent = this.wrapTextAtCharLimitPreserveStyles(
                        editor,
                        maxCharCount,
                        maxBulletCharCount
                    );

                    // Update tooltip
                    this.throwValidationErrorIfRequired(editor, simulatedWrappedContent, maxLineCount);

                    if (simulatedWrappedContent.length && simulatedWrappedContent.length > maxLineCount) {
                        editor.fire('lineAndCharExceeded', {
                            errorMessage: 'LINE_NUMBER_EXCEEDED'
                        });

                        if (
                            this.previousEditorData &&
                            editor &&
                            this.previousEditorWrappedText.length <= maxLineCount
                        ) {
                            hasOverflow = true;
                            this.isRestoringContent = true;
                            editor.model.change(writer => {
                                this.updateEditorOriginalContents(editor, writer, this.previousEditorData);

                                // throw validation error if required
                                this.throwValidationErrorIfRequired(
                                    editor,
                                    this.previousEditorWrappedText,
                                    maxLineCount
                                );

                                // Restore cursor to the document end (user was typing at the end)
                                try {
                                    writer.setSelection(this.selectionBeforeCursorPosition);
                                    editor.editing.view.focus();
                                    editor.editing.view.scrollToTheSelection();
                                    this.updateLineNumberAndCharCounterIndicatorView(
                                        editor,
                                        this.previousEditorWrappedText,
                                        parentView,
                                        maxLineCount
                                    );
                                } catch (error) {
                                    console.error(
                                        `Error restoring cursor to the document position ${this.selectionBeforeCursorPosition.path.toString()}: ${error}`
                                    );
                                }
                            });
                        }
                    }

                    if (editor) {
                        // Persisting immediate previous editor state
                        this.previousEditorData = (editor as any)?.getData() ?? '';
                        this.previousEditorWrappedText = !hasOverflow
                            ? simulatedWrappedContent
                            : this.wrapTextAtCharLimitPreserveStyles(editor, maxCharCount, maxBulletCharCount);

                        if (!hasOverflow) {
                            this.updateLineNumberAndCharCounterIndicatorView(
                                editor,
                                simulatedWrappedContent,
                                parentView,
                                maxLineCount
                            );
                        }

                        hasOverflow = false;
                    }

                    // Persisting immediate previous cursor position
                    if (editor) {
                        this.selectionBeforeCursorPosition = editor.model.document.selection.getFirstPosition();
                    }
                } catch (error) {
                    console.error(error);
                }
            };

            // Listen to change:data to update cursor information and reset the restoration flag
            editor.model.document.on('change:data', () => {
                // Update cursor information when content changes (not just selection)
                // Note: We check isRestoringContent here to prevent recursive calls during content restoration
                if (!this.isRestoringContent) {
                    updateLineNumberAndCharCounterIndicator();
                }

                // Reset the flag after data change is complete
                if (this.isRestoringContent) {
                    // Use microtask to ensure all events have fired
                    Promise.resolve().then(() => {
                        this.isRestoringContent = false;
                    });
                }
            });

            editor.model.document.selection.on('change:range', updateLineNumberAndCharCounterIndicator);
            editor.on('updateLineAndCharConfig', updateLineNumberAndCharCounterIndicator);

            // Call updateLineNumberAndCharCounterIndicator initially when editor is ready
            editor.on('ready', () => {
                updateLineNumberAndCharCounterIndicator();
            });

            // Also call it immediately if editor is already ready
            if (editor.state === 'ready') {
                // Use setTimeout to ensure the view is fully rendered
                setTimeout(() => {
                    updateLineNumberAndCharCounterIndicator();
                }, 0);
            }

            return parentView;
        });
    }

    private updateLineNumberAndCharCounterIndicatorView(
        editor: any,
        simulatedWrappedContent: any[],
        parentView: any,
        maxLineCount: number
    ): void {
        const info = this.getCursorInfoInFinalLines(editor, simulatedWrappedContent);

        const lineInfo = {
            lineIndex: info.lineIndex >= 0 ? info.lineIndex + 1 : 1,
            maxLineCount: maxLineCount,
            charCount: Math.max(info.charCount, 0),
            currentCharPosition: Math.max(info.currentCharPosition, 0),
            maximumCharCount: Math.max(info.maximumCharCount, 0)
        };

        editor.fire('onCursorInfoChanged', { ...lineInfo, totalLineCount: simulatedWrappedContent.length });

        if (parentView.element) {
            let lineCountMessage = `Line(s): ${lineInfo?.lineIndex}/${lineInfo?.maxLineCount}, Position: ${lineInfo?.currentCharPosition}, Char(s): ${lineInfo?.charCount}/${lineInfo?.maximumCharCount}`;
            parentView.element.innerHTML = `<span style="font-family: monospace;">${lineCountMessage}</span>`;
        }
    }

    private throwValidationErrorIfRequired(editor: any, updatedText: any[], maxLineCount: number): void {
        // we need to throw any event to update the validation of the form control
        const previousErrorState = this.hasTooltipError;
        this.hasTooltipError = updatedText.length > maxLineCount;

        // Update form control validation if error state changed
        if (previousErrorState !== this.hasTooltipError) {
            // need to throw any event to update the validation of the form control
            // this.updateFormControlValidation();
            editor.fire('updateFormControlValidation', {
                updatedText: updatedText,
                maxLineCount: maxLineCount,
                hasValidationError: this.hasTooltipError,
                errorMessage: this.hasTooltipError ? 'LINE_NUMBER_EXCEEDED' : ''
            });
        }
    }

    private updateEditorOriginalContents(editor: any, writer: any, replaceWithEditorData: any): void {
        // clearing the current content
        const root = editor.model.document.getRoot();
        writer.remove(writer.createRangeIn(root));
        // inserting new content
        const viewFragment = editor.data.processor.toView(replaceWithEditorData);
        const modelFragment = editor.data.toModel(viewFragment);
        writer.insert(modelFragment, root, 'end');
    }

    public wrapTextAtCharLimitPreserveStyles(
        editor: any,
        maxParagraphCharCount: number = -1,
        maxBulletCharCount: number = -1
    ): any[] {
        let finalLineText = [];
        const model = editor.model;
        const root = model.document.getRoot();
        const blocks: Element[] = Array.from(root?.getChildren() ?? []);

        // Track list groups for unique key generation
        let listGroupCounter = 0;
        let currentListGroup: string | null = null;
        let currentListType: string | null = null;
        let currentListIndent: number = 0;
        // Track per-list-item ids to establish parent/child relation across wrapped segments
        let listItemIdCounter = 0;

        for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
            const block = blocks[blockIndex];
            const children = Array.from(block?.getChildren());
            const textNodes: any[] = [];
            const listIndent = Number(block?.getAttribute('listIndent') ?? 0);
            const isListItem = block?.getAttribute('listType') !== undefined;
            const listType = isListItem ? String(block?.getAttribute('listType') || '') : null;

            const maximumCharCount = isListItem
                ? Math.max(1, maxBulletCharCount - listIndent * 4) // Reduce 5 chars per nesting level, but never go below 1
                : maxParagraphCharCount;

            // Generate or continue list group key
            let listGroupKey: string | null = null;
            if (isListItem) {
                // Check if this is a continuation of the same list group
                if (currentListType === listType && currentListIndent === listIndent) {
                    listGroupKey = currentListGroup;
                } else {
                    // Start a new list group
                    listGroupCounter++;
                    listGroupKey = `list-group-${listGroupCounter}`;
                    currentListGroup = listGroupKey;
                    currentListType = listType;
                    currentListIndent = listIndent;
                }
            } else {
                // Reset list tracking for non-list items
                currentListGroup = null;
                currentListType = null;
                currentListIndent = 0;
            }

            // Collect all $text nodes with their styling information
            for (const child of children) {
                if (child.is('$text')) {
                    textNodes.push({
                        text: child.data,
                        attributes: this.getTextAttributes(child),
                        parentAttributes: this.getParentAttributes(child)
                    });
                }
            }

            const fullText = textNodes.map(n => n.text).join('');

            // Process each text node word by word with individual styling
            const wordsWithIndividualStyling = this.processTextNodesWordByWord(textNodes, isListItem);

            // Wrap text for this block when maximumCharCount is exceeded
            if (maximumCharCount > 0 && fullText.length > maximumCharCount) {
                const wrappedLines = this.wrapTextWordByWord(wordsWithIndividualStyling, maximumCharCount);

                // Annotate each wrapped segment. For list items, also provide parent/child relationship metadata.
                const segmentsCount = wrappedLines.length;
                const currentListItemId = isListItem ? `list-item-${++listItemIdCounter}` : null;

                wrappedLines.forEach((line, lineIndex) => {
                    line.lineContext = {
                        listGroupKey: listGroupKey,
                        listType: listType,
                        listIndent: listIndent,
                        blockIndex: blockIndex
                    };
                    line.isListItem = isListItem;
                    line.maximumCharCount = maximumCharCount;

                    if (isListItem) {
                        const parentId = currentListItemId;
                        const itemId = lineIndex === 0 ? currentListItemId : `${currentListItemId}-cont-${lineIndex}`;
                        line.grouped = {
                            groupKey: listGroupKey,
                            parentId: parentId,
                            itemId: itemId,
                            segmentIndex: lineIndex,
                            segmentsCount: segmentsCount
                        };
                    }
                });

                finalLineText.push(...wrappedLines);
            } else {
                // If no wrapping needed, add the full text as one line with word-by-word styling
                const baseLine: any = {
                    text: fullText,
                    words: wordsWithIndividualStyling,
                    styling: this.mergeStylingFromNodes(textNodes),
                    isListItem,
                    lineContext: {
                        listIndent,
                        listGroupKey,
                        listType,
                        blockIndex
                    },
                    maximumCharCount // keeping this, otherwise we need to calculate aging for nesting bullet point max char count
                };

                if (isListItem) {
                    const currentListItemId = `list-item-${++listItemIdCounter}`;
                    baseLine.grouped = {
                        groupKey: listGroupKey,
                        parentId: currentListItemId,
                        itemId: currentListItemId,
                        segmentIndex: 0,
                        segmentsCount: 1
                    };
                }

                finalLineText.push(baseLine);
            }
        }

        return finalLineText;
    }

    /**
     * Processes text nodes and splits them into individual words with styling
     */
    private processTextNodesWordByWord(textNodes: any[], isListItem: boolean = false): any[] {
        const words: any[] = [];

        for (const node of textNodes) {
            // Split text by spaces while preserving styling for each word
            const wordParts = node.text.split(/(\s+)/);

            for (const part of wordParts) {
                if (part.trim() === '') {
                    // Handle spaces/whitespace
                    words.push({
                        text: part,
                        attributes: node.attributes,
                        parentAttributes: node.parentAttributes,
                        isWhitespace: true,
                        isListItem: isListItem
                    });
                } else {
                    // Handle actual words
                    words.push({
                        text: part,
                        attributes: { ...node.attributes },
                        parentAttributes: { ...node.parentAttributes },
                        isWhitespace: false,
                        isListItem: isListItem
                    });
                }
            }
        }

        return words;
    }

    /**
     * Wraps text word by word when it exceeds the maximum character count
     */
    private wrapTextWordByWord(wordsWithStyling: any[], maxLength: number): any[] {
        const result: any[] = [];
        let currentLine: any[] = [];
        let currentLineLength = 0;

        for (let i = 0; i < wordsWithStyling.length; i++) {
            const wordObj = wordsWithStyling[i];
            const word = wordObj.text;

            // If adding this word would exceed the max length
            if (currentLineLength + word.length > maxLength) {
                // If current line is not empty, add it to results and start a new line
                if (currentLine.length > 0) {
                    result.push({
                        text: currentLine.map(w => w.text).join(''),
                        words: currentLine,
                        styling: this.mergeStylingFromWords(currentLine),
                        hasWrappedContent: true,
                        isListItem: currentLine.some(w => w.isListItem) || false
                    });
                    currentLine = [wordObj];
                    currentLineLength = word.length;
                } else {
                    // If current line is empty, this word is too long for one line
                    // Split it if possible, otherwise add it as is
                    if (word.length > maxLength) {
                        const splitWords = this.splitLongWordWithIndividualStyling(wordObj, maxLength);
                        result.push(...splitWords);
                        currentLine = [];
                        currentLineLength = 0;
                    } else {
                        currentLine = [wordObj];
                        currentLineLength = word.length;
                    }
                }
            } else {
                // Word fits on current line
                currentLine.push(wordObj);
                currentLineLength += word.length;
            }
        }

        // Add the last line if it's not empty
        if (currentLine.length > 0) {
            result.push({
                text: currentLine.map(w => w.text).join(''),
                words: currentLine,
                styling: this.mergeStylingFromWords(currentLine),
                hasWrappedContent: currentLine.length > 1,
                isListItem: currentLine.some(w => w.isListItem) || false
            });
        }

        return result;
    }

    /**
     * Splits a word that is longer than the maximum character count while preserving individual styling
     */
    private splitLongWordWithIndividualStyling(wordObj: any, maxLength: number): any[] {
        const result: any[] = [];
        let remainingWord = wordObj.text;
        let startIndex = 0;

        while (remainingWord.length > 0) {
            const chunk = remainingWord.substring(0, maxLength);
            result.push({
                text: chunk,
                words: [
                    {
                        text: chunk,
                        attributes: { ...wordObj.attributes },
                        parentAttributes: { ...wordObj.parentAttributes },
                        isWhitespace: false,
                        isListItem: wordObj.isListItem || false,
                        isSplitWord: true,
                        originalWord: wordObj.text,
                        startIndex,
                        endIndex: startIndex + chunk.length
                    }
                ],
                styling: {
                    ...wordObj.attributes,
                    ...wordObj.parentAttributes,
                    isSplitWord: true,
                    originalWord: wordObj.text,
                    startIndex,
                    endIndex: startIndex + chunk.length
                },
                hasWrappedContent: true
            });
            remainingWord = remainingWord.substring(maxLength);
            startIndex += maxLength;
        }

        return result;
    }

    /**
     * Gets text-level attributes (bold, italic, etc.)
     */
    private getTextAttributes(textNode: any): any {
        const attributes: any = {};

        // Text formatting attributes
        if (textNode.hasAttribute('bold')) attributes.bold = true;
        if (textNode.hasAttribute('italic')) attributes.italic = true;
        if (textNode.hasAttribute('underline')) attributes.underline = true;
        if (textNode.hasAttribute('strikethrough')) attributes.strikethrough = true;
        if (textNode.hasAttribute('code')) attributes.code = true;

        // Font attributes
        if (textNode.hasAttribute('fontSize')) attributes.fontSize = textNode.getAttribute('fontSize');
        if (textNode.hasAttribute('fontFamily')) attributes.fontFamily = textNode.getAttribute('fontFamily');
        if (textNode.hasAttribute('fontColor')) attributes.fontColor = textNode.getAttribute('fontColor');
        if (textNode.hasAttribute('fontBackgroundColor'))
            attributes.fontBackgroundColor = textNode.getAttribute('fontBackgroundColor');

        // Additional text attributes that might exist
        if (textNode.hasAttribute('superscript')) attributes.superscript = true;
        if (textNode.hasAttribute('subscript')) attributes.subscript = true;
        if (textNode.hasAttribute('highlight')) attributes.highlight = true;

        return attributes;
    }

    /**
     * Gets parent element attributes (alignment, etc.)
     */
    private getParentAttributes(textNode: any): any {
        const parent = textNode.parent;
        const attributes: any = {};

        // Block-level attributes
        if (parent.hasAttribute('alignment')) attributes.alignment = parent.getAttribute('alignment');
        if (parent.hasAttribute('indent')) attributes.indent = parent.getAttribute('indent');
        if (parent.hasAttribute('listType')) attributes.listType = parent.getAttribute('listType');
        if (parent.hasAttribute('listIndent')) attributes.listIndent = parent.getAttribute('listIndent');

        // Additional block attributes
        if (parent.hasAttribute('blockQuote')) attributes.blockQuote = true;
        if (parent.hasAttribute('heading')) attributes.heading = parent.getAttribute('heading');
        if (parent.hasAttribute('paragraph')) attributes.paragraph = true;

        return attributes;
    }

    /**
     * Merges styling information from multiple text nodes
     */
    private mergeStylingFromNodes(textNodes: any[]): any {
        if (textNodes.length === 0) return {};

        // Use the first node's styling as base, but merge common attributes
        const baseStyling = { ...textNodes[0].attributes, ...textNodes[0].parentAttributes };

        // Check if all nodes have consistent styling
        const allConsistent = textNodes.every(
            node =>
                JSON.stringify(node.attributes) === JSON.stringify(textNodes[0].attributes) &&
                JSON.stringify(node.parentAttributes) === JSON.stringify(textNodes[0].parentAttributes)
        );

        if (allConsistent) {
            return baseStyling;
        } else {
            // If styling is inconsistent, return mixed styling indicator
            return {
                ...baseStyling,
                hasMixedStyling: true,
                allStyles: textNodes.map(node => ({
                    text: node.text,
                    attributes: node.attributes,
                    parentAttributes: node.parentAttributes
                }))
            };
        }
    }

    /**
     * Merges styling from words in a line
     */
    private mergeStylingFromWords(words: any[]): any {
        if (words.length === 0) return {};

        const baseStyling = { ...words[0].attributes, ...words[0].parentAttributes };

        // Check if all words have consistent styling
        const allConsistent = words.every(
            word =>
                JSON.stringify(word.attributes) === JSON.stringify(words[0].attributes) &&
                JSON.stringify(word.parentAttributes) === JSON.stringify(words[0].parentAttributes)
        );

        if (allConsistent) {
            return baseStyling;
        } else {
            return {
                ...baseStyling,
                hasMixedStyling: true,
                wordStyles: words.map(word => ({
                    text: word.text,
                    attributes: word.attributes,
                    parentAttributes: word.parentAttributes
                }))
            };
        }
    }

    public getCursorInfoInFinalLines(
        editor: any,
        finalLineText: any[]
    ): { lineIndex: number; charCount: number; currentCharPosition: number; maximumCharCount: number } {
        try {
            const model = editor.model;
            const root = model.document.getRoot();
            const blocks: Element[] = Array.from(root?.getChildren() ?? []);

            const cursorPosition = model.document.selection.getFirstPosition();
            if (!cursorPosition) return { lineIndex: -1, charCount: -1, currentCharPosition: -1, maximumCharCount: -1 };

            // Find which block the cursor is in and char offset within that block
            let cursorBlockIndex = -1;
            let cursorCharOffsetInBlock = 0;

            for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
                const block = blocks[blockIndex] as any;
                try {
                    const rangeOnBlock: any = model.createRangeOn(block);
                    if (rangeOnBlock.containsPosition(cursorPosition)) {
                        cursorBlockIndex = blockIndex;
                        const startPos: any = model.createPositionAt(block, 0);
                        const rangeToCursor: any = model.createRange(startPos, cursorPosition);
                        let charCount = 0;
                        const walker = rangeToCursor.getWalker({ ignoreElementEnd: true });
                        for (const step of Array.from(walker)) {
                            const item: any = (step as any).item;
                            const length: any = (step as any).length ?? 0;
                            if (item && item.is && (item.is('$text') || item.is('$textProxy'))) {
                                charCount += length;
                            }
                        }
                        cursorCharOffsetInBlock = Math.max(0, charCount);
                        break;
                    }
                } catch (error) {
                    console.error('Error getting cursor info in final lines', error);
                }
            }

            if (cursorBlockIndex < 0)
                return { lineIndex: -1, charCount: -1, currentCharPosition: -1, maximumCharCount: -1 };

            // Filter lines belonging to the cursor's block
            const blockLines = finalLineText
                .map((l, idx) => ({ l, idx }))
                .filter(x => x.l?.lineContext?.blockIndex === cursorBlockIndex);

            if (blockLines.length === 0)
                return { lineIndex: -1, charCount: -1, currentCharPosition: -1, maximumCharCount: -1 };

            let cumulative = 0;
            for (let i = 0; i < blockLines.length; i++) {
                const line = blockLines[i].l;
                const globalIndex = blockLines[i].idx;
                const lineText: string =
                    typeof line.text === 'string'
                        ? line.text
                        : Array.isArray(line.words)
                        ? line.words.map((w: any) => w.text).join('')
                        : '';
                const nextCumulative = cumulative + lineText.length;
                if (cursorCharOffsetInBlock <= nextCumulative || i === blockLines.length - 1) {
                    const currentCharPosition = Math.max(0, cursorCharOffsetInBlock - cumulative);
                    return {
                        lineIndex: globalIndex,
                        charCount: finalLineText[globalIndex].text.length,
                        currentCharPosition: currentCharPosition,
                        maximumCharCount: finalLineText[globalIndex].maximumCharCount
                    };
                }
                cumulative = nextCumulative;
            }

            return { lineIndex: -1, charCount: -1, currentCharPosition: 0, maximumCharCount: -1 };
        } catch (error) {
            console.error('Error getting cursor info in final lines', error);
            return { lineIndex: -1, charCount: -1, currentCharPosition: -1, maximumCharCount: -1 };
        }
    }
}
